import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiProblem } from '../api/problem.js';
import { buttonClasses } from '../components/button-styles.js';

/** Controles con los colores de la aplicación (tokens de `styles.css`); los botones son los de `button-styles.ts`. */
export const INPUT = 'mt-1 block w-full rounded-md border border-input bg-card px-2 py-1.5';
export const PRIMARY_BUTTON = buttonClasses();
export const SECONDARY_BUTTON = buttonClasses('outline');

/** El primer 403 de las consultas y mutaciones de una pantalla: se muestra una sola vez («Acceso denegado»). */
export function firstForbidden(...errors: unknown[]): ApiProblem | undefined {
  return errors.find((error): error is ApiProblem => error instanceof ApiProblem && error.status === 403);
}

export const isStale = (error: unknown) => error instanceof ApiProblem && error.status === 412;

/** Errores de forma por campo (del esquema del contrato), en texto fijo. */
export type FieldErrors = Partial<Record<string, string>>;

export function Field({ id, label, error, children }: { id: string; label: string; error?: string | undefined; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Propiedades de accesibilidad de un campo con error. */
export const describedBy = (id: string, error: string | undefined) =>
  error ? { 'aria-invalid': true, 'aria-describedby': `${id}-error` } : {};

export function Checkbox({ id, label, checked, onChange }: { id: string; label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm">
      <input id={id} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

export function Notice({ text }: { text: string | undefined }) {
  return text ? <output className="block rounded-md border border-border bg-muted p-3 text-sm">{text}</output> : null;
}

export function BackToAdmin() {
  return (
    <Link to="/administracion" className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
      Volver a administración
    </Link>
  );
}

/** Texto vacío → `null` (campo opcional y anulable, como la descripción). */
export const nullIfBlank = (value: string) => (value.trim() === '' ? null : value);
